/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import cloneDeep from 'lodash.clonedeep';
import isEmpty from 'lodash.isempty';
import {useCurrentGlobalGroupId} from '@ion/applications/src/hooks/useCurrentGlobalGroupId';
import {useDeviceDeepLink} from '@ion/applications/src/hooks/useDeviceDeepLink';
import {useGridRowsPerPage} from '@ion/applications/src/hooks/useGridRowsPerPage';
import {useViewSubGroups} from '@ion/applications/src/hooks/useViewSubGroups';
import {
  getBulkEntityDeleteMessage,
  getEntityDeleteMessage
} from '@ion/applications/src/utils/translation';
import {resolveDeviceName} from '@ion/core/src/entities/Devices';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {IApplicationFeatures} from '@ion/core/src/security/ApplicationFeatures';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  AccountLink,
  Assets,
  BulkActionEntity,
  CRUDPermissions,
  Device,
  Devices as DeviceArr,
  Entity,
  Feature,
  GenericObject,
  SCIDeviceType
} from '@ion/core/src/types/entities';
import {APIGatewaySmartSearch} from '@ion/core/src/types/services';
import {DownloadTemplate} from '@ion/core/src/types/templates';
import {DownloadTemplateDeviceTypes} from '@ion/core/src/xlsx/TemplateHelpers';
import {ContentLoader} from '@ion/dls/src/components/loading/ContentLoader';
import {AssetAssociation, AssetTreeRefresh} from '@ion/dls/src/editors/AssetEditor';
import {AuditDateFilterProps} from '@ion/dls/src/editors/audit/AuditDialog';
import {BulkDeletePrompt} from '@ion/dls/src/editors/BulkDeletePrompt';
import {DeviceDeletePrompt} from '@ion/dls/src/editors/DeviceDeletePrompt';
import {DeviceEditorOptions} from '@ion/dls/src/editors/DeviceEditor';
import {BulkHeader, GridOperation} from '@ion/dls/src/grid/IonGrid';
import {useClearFilters} from '@ion/dls/src/hooks/useClearFilters';
import {UserNotificationDialog} from '@ion/dls/src/notifications/UserNotificationDialog';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import Render from '@ion/dls/src/render/Render';
import {FilterChips} from '@ion/dls/src/types/editors';
import {
  ModularSearchProps,
  PageViewSubGroupsProps,
  ViewSubGroupsPosition
} from '@ion/dls/src/types/types';
import {getViewInheritableGroupProps, getViewSubGroupsProps} from '@ion/dls/src/utils/ComponentUtils';
import {hasFilteredExactMatch} from '@ion/features/src/search/ModularSearchWidget';
import LMUConfigDeviceFilePrefetch from '../../components/jobs/LMUConfigDeviceFilePrefetch';
import useEnableDeviceFilePrefetch from '../../components/jobs/useEnableDeviceFilePrefetch';
import useInheritableGroup from '../../hooks/useInheritableGroup';
import {dispatchGridColumnsChange} from '../../store/helpers';
import {ExportContext} from '../../types/redux';
import AdminContainer, {
  CapExportOperations,
  GridOperations,
  PageTitle,
  PageTitleSize
} from '../admin/AdminContainer';
import {editAdminAsset} from '../admin/assets/state';
import BulkEditGroupEditor from '../admin/groups/BulkEditGroupEditor';
import EditGroupEditor from '../admin/groups/EditGroupEditor';
import {groupEditShowDialogSelector} from '../admin/groups/state-selectors';
import ViewGroupEditor from '../admin/groups/ViewGroupEditor';
import {ReportOperations} from '../reports/reportsUtils';
import AddDeviceEditor from './AddDeviceEditor';
import {BulkAddDevicesEditor} from './BulkAddDevicesEditor';
import {BulkEditDeviceEditor} from './BulkEditDeviceEditor';
import {BulkSCIUpdate} from './BulkSCIUpdate';
import {DeviceActionDialogs} from './DeviceActionDialogs';
import DeviceGridError from './DeviceGridError';
import DevicePasswordEditor from './DevicePasswordEditor';
import DevicePasswordGrid from './DevicePasswordGrid';
import {getESNs} from './DevicePasswordUtils';
import DevicesGrid from './DevicesGrid';
import ImportDevicesContainer from './ImportDevicesContainer';
import ManageServicesEditor from './ManageServicesEditor';
import {
  getAnySearchCriteria,
  getExcludeDevicesQuery,
  getNewSearchCriteria,
  hasAnySearch,
  hasMaxSearch
} from './ModularSearchUtils';
import {devicesModularSearchSelector} from './selector';
import {
  addAdminDevice,
  allDevicesBulkSCIUpdate,
  bulkSCIUpdate,
  bulkSCIUpload,
  deleteAdminDevice,
  deleteAdminDevices,
  importAdminDevices,
  importDevicePassword,
  initializeDevices,
  moveAdminDevices,
  moveDevices,
  NAME,
  PREFERENCE,
  setAreTotalDeviceRowsSelected,
  setDeviceFilters,
  setDeviceSearch,
  setDevicesGridPageSize,
  setDevicesSelected,
  setDevicesViewSubGroups,
  setIncludeSubAccounts,
  setInheritableGroups,
  setTotalDeviceRowsCount,
  showBulkAddDevices,
  showManageServicesDialog,
  UPDATE_GRID_COLUMNS,
  updateBulkSCISetting,
  updateDevicesSearchQueries,
  viewBulkEditGroup,
  viewDevicePassword
} from './state';
import {
  adminDevicesAddIsEditingSelector,
  adminDevicesBulkAddIsEditingSelector,
  adminDevicesBulkDeleteErrorSelector,
  adminDevicesBulkDeleteIsEditingSelector,
  adminDevicesBulkDeleteLoadingSelector,
  adminDevicesBulkDeleteRecordSelector,
  adminDevicesBulkDeleteResultSelector,
  adminDevicesBulkDeleteSelectedSelector,
  adminDevicesBulkEditIsEditingSelector,
  adminDevicesBulkImportIsImportingSelector,
  adminDevicesDestroyErrorSelector,
  adminDevicesDestroyIsEditingSelector,
  adminDevicesDestroyLoadingSelector,
  adminDevicesDestroyResultSelector,
  adminDevicesDestroySelectedSelector,
  adminDevicesGridColumnStateSelector,
  adminDevicesGridFilterModelSelector,
  adminDevicesViewIsEditingSelector,
  bulkSCISettingDialogLoadingDialogSelector,
  bulkSCISettingLoadingDialogSelector,
  bulkSCISettingSelectedDevicesSelector,
  bulkSCISettingUpdateDialogSelector,
  bulkSCISettingUploadDialogSelector,
  devicesAreTotalRowsSelectedSelector,
  devicesFiltersSelector,
  devicesSelectedSelector,
  devicesTotalRowsCountSelector
} from './state-selectors';
import useAssociatedAssets from './useAssociatedAssets';
import useDevicesGridSearch from './useDevicesGridSearch';
import useModularSearch from './useModularSearch';

export const DEFAULT_SORT_PARAM = ['id'];

export interface DevicesProps extends Pick<DeviceEditorOptions, 'showDeviceType'>,
  AssetAssociation,
  PageViewSubGroupsProps,
  AuditDateFilterProps,
  ReportOperations,
  GridOperation,
  BulkHeader,
  GridOperations,
  DownloadTemplateDeviceTypes,
  CapExportOperations,
  AssetTreeRefresh,
  ModularSearchProps {
  onSelectAccount?: (value: AccountLink) => void;
  openBulkActionDialog: Function;
  refreshInitialization?: Function;
  accountHierarchy?: AccountLink[];
  globalGroup: AccountLink,
  bulkEditGroupSelected?: Assets | DeviceArr;
  manageServicesSelectedDevices?: DeviceArr;
  Features: Feature;
  pageTitle?: PageTitle;
  pageTitleSize?: PageTitleSize;
  selectedIdentifiers?: string[] | number[];
  excludedColumns?: string[];
  filterableColumns?: string[];
  rowMenusEnabled?: boolean;
  searchKeyword?: string;
  identifierType?: string;
  rowPerPageTextTransform?: any;
  disableDeviceAccountChooser?: boolean;
  isCtcAdmin: boolean;
  isIonSuite: boolean;
  showSCISettings?: boolean;
  showAdd?: boolean;
  enableDefaultSort?: boolean;
  disableImport?: boolean;
  showFullWidthSearch?: boolean;
  enableSelectionsExport?: boolean;
  enableAllHyperLinks?: boolean;
  enabledHyperLinks?: string[];
  isHeartBeat?: boolean;
  isMultiEditEnabled?: boolean;
  includeGroupProperties?: boolean;
  includeGlobalSearch?: boolean;
  includeModularSearch?: boolean;
  omitAdminCTAs?: boolean;
  omitGridHeaderCTAs?: boolean;
  enableBulkAdd?: boolean;
  showImportDevicePassword?: boolean;
  showViewDevicePassword?: boolean;
  showBulkEditGroup?: boolean;
  showManageServices?: boolean;
  checkAssetAssociation?: boolean;
  showGroup?: boolean;
  includeDeviceGridCheckboxes?: boolean;
  includeAssetGridCheckboxes?: boolean;
  includeDeviceType?: boolean;
  enableSelectAll?: boolean;
  usePreferedPageSize?: boolean;
  includeSwapDevice?: boolean;
  includeSpreaderChooser?: boolean;
  isMultiSelectSpreader?: boolean;
  includeGroups?: boolean;
  defaultLabel?: boolean;
  selectedJobId?: (jobId: number) => void;
  isSortByParams?: boolean;
  devicesPermissions?: CRUDPermissions;
  hasVehicleProfile?: boolean;
  hasCreateJob?: boolean;
  accountsPermissions?: CRUDPermissions;
  groupsPermissions?: CRUDPermissions;
  devicePasswordPermissions?: CRUDPermissions;
  hasExportLimit?: boolean;
  omitHeader?: boolean;
  showEOLDevices?: boolean;
  platformResults?: GenericObject[];
  includeReportingSection?: boolean;
  isMultiSelectDevice?: boolean;
  showDefaultDefinitions?: boolean;
  hasMultiColumnPreferences?: boolean;
  hasInheritableGroups?: boolean;
  enableExclude?: boolean;
  prefetchDeviceFiles?: boolean;
  reloadOnVisible?: string[];
  accountsMap?: GenericObject;
  isGridInsideADialog?: boolean;
  isVbuFwEnabled?: boolean;
};

/**
 * Defines the Devices container component. This component is only to render
 * child components and provide top level subscriptions and dispatches to the
 * redux store.
 */
export const Devices = ({
  accountHierarchy,
  globalGroup,
  Features,
  onSelectAccount,
  openBulkActionDialog,
  selectedJobId,
  pageTitle,
  pageTitleSize,
  selectedIdentifiers,
  identifierType,
  bulkEditGroupSelected,
  accountsMap,
  manageServicesSelectedDevices,
  excludedColumns,
  rowMenusEnabled,
  enableSelectionsExport,
  enableBulkAdd,
  groupsPermissions,
  accountsPermissions,
  devicePasswordPermissions,
  refreshInitialization,
  devicesPermissions,
  hasVehicleProfile,
  hasCreateJob,
  searchKeyword = '',
  filterableColumns = [],
  platformResults = [],
  reloadOnVisible = [],
  enabledHyperLinks = [],
  rowPerPageTextTransform = 'uppercase',
  viewSubGroupsPosition = ViewSubGroupsPosition.GRID_PAGINATION_CONTROLS,
  enableExclude = false,
  isDeviceImport = false,
  showDeviceType = false,
  showAdd = true,
  disableImport = false,
  showFullWidthSearch = false,
  enableAllHyperLinks = false,
  isHeartBeat = false,
  disableDeviceAccountChooser = false,
  isMultiEditEnabled = false,
  isCtcAdmin = false,
  isIonSuite = false,
  showSCISettings = false,
  includeGlobalSearch = false,
  includeModularSearch = false,
  omitAdminCTAs = false,
  omitGridHeaderCTAs = false,
  showImportDevicePassword = false,
  hasInheritableGroups = false,
  showViewDevicePassword = false,
  showBulkEditGroup = false,
  showManageServices = false,
  checkAssetAssociation = false,
  showGroup = false,
  includeDeviceGridCheckboxes = false,
  includeAssetGridCheckboxes = false,
  includeDeviceType = false,
  isVbuFwEnabled = false,
  includeReportingSection = false,
  enableSelectAll = false,
  usePreferedPageSize = false,
  includeSwapDevice = false,
  includeSpreaderChooser = false,
  isMultiSelectSpreader = false,
  defaultLabel = false,
  includeGroups = false,
  isSortByAlphaNumeric = false,
  enableSubgroupToggle = false,
  includeGroupProperties = true,
  includeAssetAssociation = false,
  showBulkHeader = false,
  omitHeader = false,
  enableDateFilter = false,
  isSortByParams = false,
  isPersist = false,
  hasExportLimit = false,
  disableExport = false,
  showBulkDialog = false,
  showEOLDevices = false,
  showTreeRefresh = false,
  autoSelectNext = false,
  enableDefaultSort = false,
  showDefaultDefinitions = false,
  isMultiSelectDevice = false,
  hasMultiColumnPreferences = false,
  prefetchDeviceFiles = false,
  isGridInsideADialog = false
}: DevicesProps) => {
  const dispatch                 = useDispatch();
  const isMounted                = useHasMounted();
  const gridRef                  = useRef<GenericObject>();
  const hasSavedQuery            = useRef(null);
  const onClearFilters           = useClearFilters(gridRef);
  const result                   = useSelector(adminDevicesDestroyResultSelector);
  const selected                 = useSelector(adminDevicesDestroySelectedSelector);
  const isEditing                = useSelector(adminDevicesDestroyIsEditingSelector);
  const loading                  = useSelector(adminDevicesDestroyLoadingSelector);
  const error                    = useSelector(adminDevicesDestroyErrorSelector);
  const bulkDeleteResult         = useSelector(adminDevicesBulkDeleteResultSelector);
  const bulkDeleteSelected       = useSelector(adminDevicesBulkDeleteSelectedSelector);
  const bulkDeleteIsEditing      = useSelector(adminDevicesBulkDeleteIsEditingSelector);
  const bulkDeleteLoading        = useSelector(adminDevicesBulkDeleteLoadingSelector);
  const bulkDeleteError          = useSelector(adminDevicesBulkDeleteErrorSelector);
  const bulkDeleteRecord         = useSelector(adminDevicesBulkDeleteRecordSelector);
  const showAddDevice            = useSelector(adminDevicesAddIsEditingSelector);
  const shouldShowBulkAddDevices = useSelector(adminDevicesBulkAddIsEditingSelector);
  const showViewDevice           = useSelector(adminDevicesViewIsEditingSelector);
  const showBulkEditDevice       = useSelector(adminDevicesBulkEditIsEditingSelector);
  const showImportDevice         = useSelector(adminDevicesBulkImportIsImportingSelector);
  const filterModel              = useSelector(adminDevicesGridFilterModelSelector);
  const {exportLimit}            = useSelector(({exporter}) => exporter.current);
  const isFilterApplied          = !isEmpty(filterModel);
  const columnState              = useSelector(adminDevicesGridColumnStateSelector);
  const preference               = isCtcAdmin ? PREFERENCE : '';

  const enablePrefetching = useEnableDeviceFilePrefetch(
    prefetchDeviceFiles, globalGroup, Features as unknown as IApplicationFeatures
  );

  const [searchFilters, setSearchFilters]         = useState({});
  const [sortOn, setSortOn]                       = useState(['name']);
  const [excludedRows, setExcludedRows]           = useState([]);
  const [isGridLoaded, setIsGridLoaded]           = useState(false);
  const [showDeleteError, setShowDeleteError]     = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const hasDeleted                                = showDeleteSuccess || showDeleteError;

  const [viewSubGroups, toggleViewSubGroups] = useViewSubGroups({
    name       : NAME,
    action     : setDevicesViewSubGroups,
    view       : !Features || enableSubgroupToggle,
    preference : preference
  });

  const [gridPageSize, setGridPageSize] = useGridRowsPerPage({
    name       : NAME,
    action     : setDevicesGridPageSize,
    preference : preference
  });

  const [viewInheritableGroup, toggleViewInheritableGroup] = useInheritableGroup({
    name       : NAME,
    action     : setInheritableGroups,
    preference : preference
  });

  const selectedDevices    = useSelector(devicesSelectedSelector);
  const setSelectedDevices = useCallback((_selectedDevices: Device[]) => {
    dispatch(setDevicesSelected(_selectedDevices));
  }, []);

  const areTotalRowsSelected    = useSelector(devicesAreTotalRowsSelectedSelector);
  const setAreTotalRowsSelected = useCallback((_areTotalRowsSelected: boolean) => {
    dispatch(setAreTotalDeviceRowsSelected(_areTotalRowsSelected));
  }, []);

  const totalRowsCount    = useSelector(devicesTotalRowsCountSelector);
  const setTotalRowsCount = useCallback((_totalRowsCount: number) => {
    dispatch(setTotalDeviceRowsCount(_totalRowsCount));
  }, []);

  const filterChips    = useSelector(devicesFiltersSelector);
  const setFilterChips = useCallback((_filters: FilterChips) => {
    dispatch(setDeviceFilters(_filters));
  }, []);

  const bulkUpload              = useSelector(bulkSCISettingUploadDialogSelector);
  const bulkUpdate              = useSelector(bulkSCISettingUpdateDialogSelector);
  const bulkSCISelectedDevices  = useSelector(bulkSCISettingSelectedDevicesSelector);
  const bulkUpdateLoading       = useSelector(bulkSCISettingLoadingDialogSelector);
  const bulkUpdateDialogLoading = useSelector(bulkSCISettingDialogLoadingDialogSelector);
  const modularSearchParams     = useSelector(devicesModularSearchSelector);
  const showEditGroup           = useSelector(groupEditShowDialogSelector);
  const accountID               = useCurrentGlobalGroupId();

  useDeviceDeepLink();

  const {
    showAssociatedAssetError,
    showBulkDeleteError,
    unassociatedDevices,
    associatedTitle,
    getAssociatedMessage,
    onDelete,
    onBulkDelete,
    onDismiss
  } = useAssociatedAssets({
    selected,
    result,
    error,
    bulkDeleteSelected,
    bulkDeleteResult,
    bulkDeleteError,
    isEditing,
    bulkDeleteIsEditing,
    checkAssetAssociation
  });

  const onRefresh = useCallback(function() {
    gridRef.current?.onRefresh(true);
    setExcludedRows([]);
  }, []);

  const {
    getSearchParams,
    mappings,
    filtersSearchCriteria
  } = useModularSearch({
    onRefresh,
    viewSubGroups,
    isVbuFwEnabled,
    isScriptVersion : !isCtcAdmin
  });

  const search              = useDevicesGridSearch(sortOn, gridRef.current?.columnKeys, isVbuFwEnabled);
  const gridQueryWithIdSort = useDevicesGridSearch(DEFAULT_SORT_PARAM, gridRef.current?.columnKeys, isVbuFwEnabled);

  useEffect((): void => {
    dispatch(setDeviceSearch(filtersSearchCriteria?.searchCriteria?.[0]));
  }, [filtersSearchCriteria]);

  useEffect((): void => {
    dispatch(setIncludeSubAccounts(viewSubGroups));
  }, [viewSubGroups]);

  useEffect(() => {
    if (!isMounted && !includeGlobalSearch) {
      dispatch(initializeDevices());
    }
  }, [isMounted, includeGlobalSearch]);

  useEffect(() => {
    return function() {
      dispatch(moveDevices(null) as any);
      dispatch(moveAdminDevices(null, false));
      dispatch(editAdminAsset(null, false, false));
    };
  }, []);

  useEffect(() => {
    if (filterChips?.length && isEmpty(searchFilters)) {
      const hasExactMatch = hasFilteredExactMatch({
        queries       : filterChips,
        pageName      : PageTitle.devices,
        hasSavedQuery : hasSavedQuery.current
      });
      const chipsParams   = mappings.transformQueriesToRequestParams(filterChips, hasExactMatch);
      dispatch(updateDevicesSearchQueries(chipsParams));
    }
  }, [includeGlobalSearch, filterChips]);

  useEffect(() => {
    error ? setShowDeleteError(true) : setShowDeleteError(false);
  }, [error]);

  useEffect(() => {
    if (result && !loading) {
      setShowDeleteSuccess(true);
    }
  }, [result]);

  /**
   * Invoke when the user chooses to close the snackbar
   * that appear after a device is deleted.
   */
  const onCloseDeleteSnackbar = useCallback(function() {
    setShowDeleteError(false);
    setShowDeleteSuccess(false);
  }, [result, error]);

  /**
   * @private
   */
  const onImport = function() {
    dispatch(importAdminDevices() as any);
  };

  /**
   * @private
   * @fetchSearchParams
   * Save search filter params to be used for CVS Exports.
   */
  const fetchSearchParams = (params) => {
    setSearchFilters(params);
    getSearchParams(params);
  };

  /**
   * Save Identifier chip selection params.
   */
  const fetchIdentifierParams = (param: string) => {
    hasSavedQuery.current = param;
  };

  /**
   * @private
   */
  const onExport = function(downloadParams?: DownloadTemplate) {
    gridRef.current?.onExport(downloadParams);
  };

  /**
   * @private
   */
  const onAdd = function() {
    dispatch(addAdminDevice());
  };

  /**
   * @private
   */
  const onSwitchToBulkAdd = useCallback(() => {
    dispatch(addAdminDevice(false));
    dispatch(showBulkAddDevices() as any);
  }, []);

  /**
   * @private
   */
  const onSwitchToSingleAdd = useCallback(() => {
    dispatch(showBulkAddDevices(false) as any);
    dispatch(addAdminDevice());
  }, []);

  /**
   * @private
   */
  const onUpdateGridColumns = function() {
    dispatch(dispatchGridColumnsChange(columnState, NAME, UPDATE_GRID_COLUMNS) as any);
  };

  /**
   * @private
   */
  const onDeleteConfirm = function(device: Device) {
    onDelete();
  };

  /**
   * @private
   */
  function onDeleteCancel() {
    dispatch(deleteAdminDevice(selected, false));
  };

  /**
   * @private
   */
  const onBulkDeleteConfirm = function(notifications: DeviceArr) {
    onBulkDelete();
  };

  /**
   * @private
   */
  function onBulkDeleteCancel() {
    dispatch(deleteAdminDevices(bulkDeleteSelected, false));
  };

  const getDeleteMessage = function() {
    if (isCtcAdmin) {
      if (selected && isEditing) {
        return getEntityDeleteMessage({name: resolveDeviceName(selected)});
      }
      if (bulkDeleteSelected && bulkDeleteIsEditing) {
        const devices: Device[] = cloneDeep(bulkDeleteSelected).map((device: Device) => {
          device.name = resolveDeviceName(device);
          return device;
        });
        return getBulkEntityDeleteMessage(Entity.device, devices);
      }
    }
  };

  const onDevicePasswordImport = useCallback(() => {
    if (selectedDevices?.length) {
      dispatch(viewDevicePassword(getESNs(selectedDevices), 'esn'));
    } else {
      dispatch(importDevicePassword(true));
    }
  }, [selectedDevices]);

  const onDevicePasswordImportClose = useCallback(() => {
    dispatch(importDevicePassword(false));
  }, []);

  const onDevicePasswordGridClose = useCallback(() => {
    dispatch(viewDevicePassword(null, '', false));
    gridRef.current.unSelectRows();
  }, []);

  const onBulkEditGroupClose = useCallback(() => {
    dispatch(viewBulkEditGroup(null, false));
  }, []);

  const onBulkServiceClose = useCallback(() => {
    dispatch(showManageServicesDialog(null, false));
  }, []);

  const viewSubGroupsProps = useMemo(() => getViewSubGroupsProps(
    viewSubGroupsPosition,
    viewSubGroups as boolean,
    toggleViewSubGroups as Function
  ), [viewSubGroupsPosition, viewSubGroups, toggleViewSubGroups]);

  const viewInheritableGroupProps =  useMemo(() => getViewInheritableGroupProps(
    viewSubGroupsPosition,
    viewInheritableGroup as boolean,
    toggleViewInheritableGroup as Function
  ), [viewSubGroupsPosition, viewInheritableGroup, toggleViewInheritableGroup]);

  const onSCIUpdateClick = useCallback(() => {
    if (selectedDevices?.length) {
      dispatch(bulkSCIUpdate(selectedDevices, true));
    } else if (areTotalRowsSelected) {
      const allSCITypes = Object.values(SCIDeviceType);
      dispatch(allDevicesBulkSCIUpdate(filtersSearchCriteria, allSCITypes, accountID) as any);
    } else {
      dispatch(bulkSCIUpload(true));
    }
  }, [selectedDevices, areTotalRowsSelected, filtersSearchCriteria, accountID]);

  const excludeRows = useCallback((rows) => {
    setExcludedRows(rows);
  }, [excludedRows]);

  const onBulkSCIClose = useCallback(() => {
    dispatch(bulkSCIUpdate(null, false));
  }, []);

  const onBulkSCIUpdate = useCallback((commandPayload) => {
    dispatch(updateBulkSCISetting(commandPayload, onRefresh) as any);
  }, []);

  const onBulkActionsClick = useCallback(() => {
    const title = translate(`common:editors.bulkActions.gridSelection.${areTotalRowsSelected
      ? 'selectedAll' : 'selectedEntities'}`,
    {account: globalGroup?.title, entity: NAME});
    const isAny       = hasAnySearch(searchFilters) || excludedRows?.length;
    const isMaxSearch = hasMaxSearch(searchFilters);
    let anySearch   = isAny ? getAnySearchCriteria(search, viewSubGroups) as APIGatewaySmartSearch
      : filtersSearchCriteria;
    let excludedIds = [];
    if (excludedRows?.length) {
      excludedIds = excludedRows.map(({id}) => id);
      anySearch   = getExcludeDevicesQuery({
        search    : searchFilters,
        accountId : accountID,
        excludedIds,
        viewSubGroups,
        isVbuFwEnabled
      });
    }
    openBulkActionDialog({
      selectedChips : selectedDevices,
      filters       : filterChips,
      customSearch  : isAny || isMaxSearch,
      search        : isMaxSearch
        ? getNewSearchCriteria({
          search   : searchFilters,
          account  : accountID,
          excluded : true,
          sort     : sortOn[0] || 'name',
          isVbuFwEnabled,
          excludedIds,
          viewSubGroups
        })
        : anySearch,
      includeSubAccounts : viewSubGroups,
      entity             : BulkActionEntity.DEVICES,
      excludedDevices    : excludedRows,
      areTotalRowsSelected,
      totalRowsCount,
      onRefresh,
      title
    });
  }, [
    viewSubGroups,
    selectedDevices,
    areTotalRowsSelected,
    totalRowsCount,
    filterChips,
    filtersSearchCriteria,
    search,
    accountID,
    excludedRows
  ]);

  return (
    <>
      <AdminContainer includeExport
        grid={
          <DevicesGrid ref={gridRef}
            enableExclude={enableExclude}
            excludedRows={excludedRows}
            setExcludedRows={excludeRows}
            showBulkHeader={showBulkHeader}
            showEOLDevices={showEOLDevices}
            platformResults={platformResults}
            isPersist={isPersist}
            enableDefaultSort={enableDefaultSort}
            onBulkActionsClick={onBulkActionsClick}
            isSortByAlphaNumeric={isSortByAlphaNumeric}
            isSortByParams={isSortByParams}
            getTotalCount={setTotalRowsCount}
            includeDefaultDefinitions={showDefaultDefinitions}
            viewSubGroups={viewSubGroups}
            isVbuFwEnabled={isVbuFwEnabled}
            globalGroup={globalGroup}
            toggleViewSubGroups={toggleViewSubGroups}
            rowPerPageTextTransform={rowPerPageTextTransform}
            isCtcAdmin={isCtcAdmin}
            isIonSuite={isIonSuite}
            includeGlobalSearch={includeGlobalSearch}
            searchKeyword={searchKeyword}
            excludedColumns={excludedColumns}
            filterableColumns={filterableColumns}
            rowMenusEnabled={rowMenusEnabled}
            filterModel={filterModel}
            enableSelectionsExport={enableSelectionsExport}
            hasExportLimit={hasExportLimit}
            selectedItemsCount={enableSelectionsExport ? selectedDevices.length : null}
            anySearch={enableDefaultSort ? gridQueryWithIdSort : search}
            exportLimit={exportLimit}
            setIsGridLoaded={setIsGridLoaded}
            omitHeader={omitHeader}
            hasInheritableGroups={hasInheritableGroups}
            omitHeaderCTAs={omitGridHeaderCTAs}
            isHeartBeat={isHeartBeat}
            enableAllHyperLinks={enableAllHyperLinks}
            enabledHyperLinks={enabledHyperLinks}
            refreshInitialization={refreshInitialization}
            isMultiEditEnabled={isMultiEditEnabled}
            onSelectAccount={onSelectAccount}
            accountHierarchy={accountHierarchy}
            selectedDevices={selectedDevices}
            setSelectedDevices={setSelectedDevices}
            enableSelectAllCheckbox={enableSelectAll}
            getAreTotalRowsSelected={setAreTotalRowsSelected}
            usePreferedPageSize={usePreferedPageSize}
            includeSwapDevice={includeSwapDevice}
            onSCIUpdateClick={onSCIUpdateClick}
            areTotalRowsSelected={areTotalRowsSelected}
            searchFilters={searchFilters}
            reloadOnVisible={reloadOnVisible}
            setSortOn={setSortOn}
            sortOn={sortOn}
            gridPageSize={gridPageSize}
            setGridPageSize={setGridPageSize}
            Features={Features}
            hasCreateJob={hasCreateJob}
            devicesPermissions={devicesPermissions}
            hasVehicleProfile={hasVehicleProfile}
            accountsPermissions={accountsPermissions}
            showTreeRefresh={showTreeRefresh}
            hasMultiColumnPreferences={hasMultiColumnPreferences}
            {...viewSubGroupsProps[ViewSubGroupsPosition.GRID_PAGINATION_CONTROLS]}
            {...viewInheritableGroupProps[ViewSubGroupsPosition.GRID_PAGINATION_CONTROLS]} />
        }
        autoSelectNext={autoSelectNext}
        includeInheritableGroup={viewInheritableGroup}
        reducer={NAME}
        excludedRows={excludedRows}
        title={pageTitle || PageTitle.devices}
        titleSize={pageTitleSize}
        searchLabel={translate('admin-devices:search')}
        getFilterQueries={setFilterChips}
        hasInheritableGroups={hasInheritableGroups}
        getIdentifierParams={includeModularSearch ? fetchIdentifierParams : undefined}
        getSearchParams={includeModularSearch ? fetchSearchParams : undefined}
        searchInstance={includeModularSearch ? mappings : undefined}
        showFullWidthSearch={showFullWidthSearch}
        omitAdminCTAs={omitAdminCTAs}
        exportContext={ExportContext.device}
        onImport={onImport}
        isVbuFwEnabled={isVbuFwEnabled}
        totalRowsCount={totalRowsCount}
        selectedItemsCount={enableSelectionsExport ? selectedDevices.length : null}
        search={search}
        onExport={onExport}
        onAdd={onAdd}
        onRefresh={onRefresh}
        getExportColumnDefinitions={gridRef.current?.getExportColumnDefinitions}
        hasAddPermission={devicesPermissions?.hasAddPermission}
        devicePasswordPermissions={devicePasswordPermissions}
        showAdd={showAdd}
        disableImport={disableImport}
        isCtcAdmin={isCtcAdmin}
        isGridInsideADialog={isGridInsideADialog}
        disableExport={disableExport}
        onDevicePasswordImport={onDevicePasswordImport}
        includeGlobalSearch={includeGlobalSearch}
        onUpdateGridColumns={onUpdateGridColumns}
        areFiltersApplied={isFilterApplied}
        onClearFilters={onClearFilters}
        onShowColumnChooser={gridRef.current?.toggleColumnChooser}
        selectedDevices={selectedDevices}
        hasExportLimit={hasExportLimit}
        showBulkDialog={showBulkDialog}
        viewSubGroups={viewSubGroups}
        {...viewSubGroupsProps[ViewSubGroupsPosition.GRID_CTAS]}
        {...viewInheritableGroupProps[ViewSubGroupsPosition.GRID_CTAS]} />
      {!isCtcAdmin && (
        <DeviceActionDialogs disableAccountChooser={isCtcAdmin}
          isMultiSelectDevice={isMultiSelectDevice}
          includeSpreaderChooser={includeSpreaderChooser}
          isMultiSelectSpreader={isMultiSelectSpreader}
          includeSubAccounts={viewSubGroups}
          selectedJobId={selectedJobId}
          enableDateFilter={enableDateFilter}
          includeGroupProperties={includeGroupProperties}
          enableAuditDialog={isCtcAdmin}
          enableAdvancedViewDialog={isCtcAdmin}
          enableGridMenuRows={isCtcAdmin}
          enableJobDialog={isCtcAdmin}
          enableViewDialog={isCtcAdmin}
          enableAlertDialogs={isCtcAdmin}
          enableEventDialogs={isCtcAdmin}
          explicitReportDate={isCtcAdmin}
          includeAdditionalDeviceFields={isCtcAdmin}
          isDeviceNotes={isCtcAdmin}
          includeEditActionInViewDialog={isCtcAdmin}
          includeGPSFields={!isCtcAdmin}
          includeGroups={includeGroups}
          includeDatetimeInformation={!isCtcAdmin}
          includeSwapDevice={includeSwapDevice}
          includeAssetAssociation={includeAssetAssociation}
          isVbuFwEnabled={isVbuFwEnabled}
          showTreeRefresh={showTreeRefresh}
          refreshDevice={isCtcAdmin}
          showDifferences={isCtcAdmin}
          showSCISettings={showSCISettings}
          usePuls={isCtcAdmin}
          Features={Features}
          showDeviceType={showDeviceType}
          areTotalRowsSelected={areTotalRowsSelected}
          totalRowsCount={totalRowsCount}
          hasCreateJob={hasCreateJob}
          devicesPermissions={devicesPermissions}
          search={filtersSearchCriteria?.searchCriteria?.[0]}
          filters={filterChips} />
      )}
      {showAddDevice && (
        <AddDeviceEditor onSwitchToBulkAdd={onSwitchToBulkAdd}
          disableAccountChooser={disableDeviceAccountChooser}
          includeGroupProperties={includeGroupProperties}
          isCtcAdmin={isCtcAdmin}
          showSCISettings={showSCISettings}
          isViewDialog={showViewDevice}
          enableBulkAdd={enableBulkAdd}
          defaultLabel={defaultLabel} />
      )}
      {shouldShowBulkAddDevices && (
        <BulkAddDevicesEditor onSwitchToSingleAdd={onSwitchToSingleAdd} />
      )}
      <Render.If condition={isEditing || hasDeleted}>
        <DeviceDeletePrompt show={isEditing}
          onDeleteConfirm={onDeleteConfirm}
          onDeleteCancel={onDeleteCancel}
          overrideMessage={getDeleteMessage}
          entity={translate('common:entityLabels.device')}
          device={selected}
          loading={loading}
          showError= {showDeleteError}
          showSuccess={showDeleteSuccess}
          onClose={onCloseDeleteSnackbar} />
      </Render.If>
      {(bulkDeleteIsEditing || bulkDeleteRecord) &&
        (<BulkDeletePrompt show={bulkDeleteIsEditing}
          items={(checkAssetAssociation && showBulkDeleteError) ? unassociatedDevices : bulkDeleteSelected}
          entity={translate('common:entityLabels.devices')}
          result={bulkDeleteResult}
          loading={bulkDeleteLoading}
          error={bulkDeleteError}
          overrideMessage={
            (checkAssetAssociation && showBulkDeleteError) ? getAssociatedMessage : getDeleteMessage
          }
          deletedItems={bulkDeleteRecord}
          onDeleteConfirm={onBulkDeleteConfirm}
          onDeleteCancel={onBulkDeleteCancel} />)}
      {showBulkEditDevice && (
        <BulkEditDeviceEditor isCtcAdmin={isCtcAdmin}
          includeReportingSection={includeReportingSection}
          includeDeviceType={includeDeviceType} />
      )}
      {showImportDevicePassword && isCtcAdmin && (
        <DevicePasswordEditor isCtcAdmin={isCtcAdmin}
          onCloseDialog={onDevicePasswordImportClose} />
      )}
      {showViewDevicePassword && isCtcAdmin && (
        <DevicePasswordGrid onCloseDialog={onDevicePasswordGridClose}
          selectedIdentifiers={selectedIdentifiers}
          identifierType={identifierType}
          isCtcAdmin={isCtcAdmin} />
      )}
      {showBulkEditGroup && isCtcAdmin && (
        <BulkEditGroupEditor areTotalRowsSelected={areTotalRowsSelected}
          search={filtersSearchCriteria}
          filters={filterChips}
          onCloseDialog={onBulkEditGroupClose}
          selected={bulkEditGroupSelected}
          account={globalGroup}
          entityLabel={translate('common:entityLabels.devices')}
          entity={Entity.device}
          onRefresh={onRefresh} />
      )}
      {showManageServices && isCtcAdmin && (
        <ManageServicesEditor areTotalRowsSelected={areTotalRowsSelected}
          totalRowsCount={totalRowsCount}
          search={filtersSearchCriteria}
          filters={filterChips}
          onCloseDialog={onBulkServiceClose}
          selectedDevices={manageServicesSelectedDevices}
          account={globalGroup} />
      )}
      {showImportDevice && isCtcAdmin && (
        <ImportDevicesContainer onDownload={onExport}
          account={globalGroup}
          isDeviceImport={isDeviceImport} />
      )}
      {showAssociatedAssetError && (
        <UserNotificationDialog show
          capitalize
          title={associatedTitle()}
          message={getAssociatedMessage()}
          dismissLabel={translate('common:editors.shared.cancel')}
          onDismiss={onDismiss} />
      )}
      {showGroup && (
        <ViewGroupEditor includeDeviceGridCheckboxes={includeDeviceGridCheckboxes}
          groupsPermissions={groupsPermissions}
          accountsMap={accountsMap}
          hasInheritableGroups={hasInheritableGroups}
          includeAssetGridCheckboxes={includeAssetGridCheckboxes} />
      )}
      {showEditGroup && (
        <EditGroupEditor isCtcAdmin={isCtcAdmin}
          includeDeviceGridCheckboxes={includeDeviceGridCheckboxes}
          includeAssetGridCheckboxes={includeAssetGridCheckboxes}/>
      )}
      {(bulkUpload || bulkUpdate) && (
        <BulkSCIUpdate open
          onClose={onBulkSCIClose}
          selectedDevices={bulkSCISelectedDevices}
          isAllRowsSelected={areTotalRowsSelected}
          accountName={globalGroup?.title}
          modularSearchParams={modularSearchParams}
          onBulkSCIUpdate={onBulkSCIUpdate}
          loading={bulkUpdateLoading}
          bulkUpload={bulkUpload}/>
      )}
      {bulkUpdateDialogLoading && <ContentLoader />}
      <DeviceGridError />
      <Render.If condition={enablePrefetching && isGridLoaded}>
        <LMUConfigDeviceFilePrefetch accountId={entityId(globalGroup)}
          includeSubAccounts={viewSubGroups}
          setIsGridLoaded={setIsGridLoaded} />
      </Render.If>
    </>
  );
};

export default Devices;
